import {
  attributeValueService,
  entityService,
  attributeService,
  referenceService,
} from '../services';
import { Asset, AssetName, AssetNamePlural, AttributeRole } from './valueMap';
import { getHierarchyPath } from './formatData';

// Compared case-insensitively: the role names are display text, not codes.
const DIMENSION = AttributeRole.dimension.toLowerCase();

// What an entity with no entity type is called. Matches export_glossary, which applies the same
// fallback, so an export and the GUI describe the same asset the same way.
const ENTITY_DEFAULT_TYPE_NAME = 'Term';

/**
 * Whether an attribute is allowed to hold Attribute Values.
 *
 * Only a Dimension is. Its values enumerate what the fields it describes are permitted to contain,
 * which is meaningless for a Measure — a Measure is quantified and aggregated rather than enumerated,
 * and carries a unit instead. The API enforces the same rule on write, so anything offering the
 * "add a value" path in the GUI has to agree with it or the user hits a validation error instead of a
 * disabled control.
 *
 * Anything that is not an attribute answers false: an Attribute Value row in the flat attribute list
 * carries no role, and neither does an entity.
 *
 * @param {{ attribute_role_name?: string }} [attribute] - An attribute row as returned by the API.
 * @returns {boolean}
 */
export const canHaveAttributeValues = attribute =>
  attribute?.attribute_role_name?.toLowerCase() === DIMENSION;

/**
 * The line under an asset's name that says what kind of thing it is.
 *
 * Shared by the asset page header and the search results so the two always read the same. It answers
 * "what is this?", not "where does it live?" — the path is the breadcrumb trail's job on a page, and a
 * row of its own in a search result.
 *
 * An entity is described by its type, falling back to "Term" for an entity that has none, since that is
 * the label an untyped entity carries throughout the GUI. An attribute is described by its role and the
 * fact that it is an attribute — "Dimension" alone reads like a property of something rather than the
 * asset itself — with its unit appended when it has one, as that is a property of the same kind.
 *
 * @param {object} asset - An entity or attribute row as returned by the API.
 * @param {string} objectName - Which kind the row is, Asset.Entity or Asset.Attribute.
 * @returns {string} The subtitle text, empty when the row has nothing to describe it by.
 */
export const getAssetSubtitle = (asset, objectName) => {
  if (!asset) {
    return '';
  }

  if (objectName === Asset.Entity) {
    return asset.entity_type_name || ENTITY_DEFAULT_TYPE_NAME;
  }

  if (!asset.attribute_role_name) {
    return '';
  }

  const role = `${asset.attribute_role_name} ${AssetName[Asset.Attribute]}`;

  return asset.attribute_unit_name ? `${role} (${asset.attribute_unit_name})` : role;
};

/**
 * Works out which request the merged glossary asset form is actually asking for.
 *
 * The form presents Term, Dimension and Measure as three roles of one thing, but Term is an Entity
 * and the other two are roles of an Attribute, and those are separate resources. Each submitted row
 * therefore carries `target_object_name` saying which kind the user chose.
 *
 * Three outcomes:
 *  - adding: create against the chosen kind;
 *  - editing without changing kind: update that kind;
 *  - editing and crossing the Term/role boundary: create against the *new* kind. The row already
 *    carries `converted_from_entity_id` or `converted_from_attribute_id`, which is what tells the API
 *    to move the asset's references, custom property values and history over and delete the old row.
 *
 * `target_object_name` is not a column, so it is stripped from every row before the request.
 *
 * @param {object|object[]} data - Row(s) as submitted by the form.
 * @param {'add'|'edit'} action - Whether the form was opened to add or to edit.
 * @returns {{ service: object, payload: object, isCreate: boolean, isConversion: boolean,
 *   targetObjectName: string }}
 */
export const resolveGlossaryAssetSubmit = (data, action) => {
  const rows = Array.isArray(data) ? data : [data];

  // Mixed selections are blocked before the form opens, so one row's target speaks for all of them.
  const targetObjectName = rows[0]?.target_object_name || Asset.Attribute;
  const isConversion = rows.some(
    row => row.converted_from_entity_id || row.converted_from_attribute_id
  );

  const cleanRows = rows.map(({ target_object_name: _target, ...rest }) => rest);

  const service = targetObjectName === Asset.Entity ? entityService : attributeService;
  const payload =
    targetObjectName === Asset.Entity ? { entities: cleanRows } : { attributes: cleanRows };

  return {
    service,
    payload,
    // A conversion creates the row in its new table, so it is a create even though the user was
    // editing an existing asset.
    isCreate: action === 'add' || isConversion,
    isConversion,
    targetObjectName,
  };
};

/**
 * Shapes an entity for the glossary tree.
 *
 * Both kinds render through one column set, keyed on the entity_* names, so an attribute has to be
 * aliased onto them (see below). Keeping both shapers here means the two lists cannot drift apart —
 * they were previously duplicated in Glossary.js and Entity.js and kept in step by hand.
 *
 * @param {object} entity - Row from GET /entity/glossary/:id.
 * @param {number|string} glossaryId
 * @returns {object}
 */
const toEntityRow = (entity, glossaryId) => ({
  ...entity,
  unique_id: `${Asset.Entity}-${entity.entity_id}`,
  parent_unique_id: entity.parent_entity_id ? `${Asset.Entity}-${entity.parent_entity_id}` : null,
  object_id: entity.entity_id,
  object_name: Asset.Entity,
  link: `/glossaries/${glossaryId}/${entity.entity_id}`,
  entity_type_name: entity.entity_type_name || ENTITY_DEFAULT_TYPE_NAME,
  // The role axis the asset form presents: Entity, Dimension or Measure. An entity has no row in
  // attribute_role, but it is one of the three choices in that picker, so carrying the word here lets
  // the tree be filtered by kind under the label that picker already uses — and lets the row show the
  // same icon the picker does.
  attribute_role_name: AssetName[Asset.Entity],
  className: 'bold',
});

/**
 * Shapes an attribute for the glossary tree, aliased onto the entity_* display keys.
 *
 * Its place in the tree comes from its own parent_entity_id. An attribute with no parent is a valid
 * row at the glossary root — it belongs to the glossary whether or not anyone has filed it under an
 * entity. Only entities can have children, so an attribute is always a leaf.
 *
 * @param {object} attribute - Row from GET /attribute/glossary/:id.
 * @returns {object}
 */
const toAttributeRow = attribute => ({
  ...attribute,
  unique_id: `${Asset.Attribute}-${attribute.attribute_id}`,
  parent_unique_id: attribute.parent_entity_id
    ? `${Asset.Entity}-${attribute.parent_entity_id}`
    : null,
  object_id: attribute.attribute_id,
  object_name: Asset.Attribute,
  entity_name: attribute.attribute_name,
  entity_description: attribute.attribute_description,
  entity_owner_user_id: attribute.attribute_owner_user_id,
  entity_owner_user_fullname: attribute.attribute_owner_user_fullname,
  link: `/attributes/${attribute.attribute_id}`,
  entity_type_name: 'Attribute',
  pii: attribute.is_pii ? 'PII' : null,
  pii_tooltip: attribute.is_pii ? 'Classified as Personally Identifiable Information (PII)' : null,
});

/**
 * Shapes an attribute value as a row of the glossary tree, aliased onto the entity_* display keys.
 *
 * A value is a row of the same table its attribute is, not a table of its own: that is what keeps its
 * checkbox, its Type and its Status in the same columns as everything above it. So, like an attribute,
 * it is aliased onto the keys the tree's columns read, and the properties it has no counterpart for
 * are left unset rather than blanked — an unset property renders as nothing, which is what an entity
 * already does for PII and sensitivity.
 *
 * Its Code is deliberately not shown. The tree has no column for a technical code, and an attribute's
 * own code is not shown either; it stays searchable through the filter.
 *
 * @param {object} value - Row from GET /attribute_value/all.
 * @returns {object}
 */
const toAttributeValueRow = value => ({
  ...value,
  unique_id: `${Asset.AttributeValue}-${value.attribute_value_id}`,
  parent_unique_id: `${Asset.Attribute}-${value.attribute_id}`,
  object_id: value.attribute_value_id,
  object_name: Asset.AttributeValue,
  link: `/attributes/${value.attribute_id}/${value.attribute_value_id}`,
  entity_name: value.attribute_value_name,
  entity_description: value.attribute_value_description,
  entity_type_name: AssetName[Asset.AttributeValue],
  // The role axis the tree shows an icon for, and filters on. A value has no role of its own, and
  // inheriting its attribute's would draw a Dimension icon on something that is not one.
  attribute_role_name: AssetName[Asset.AttributeValue],
});

/**
 * How a filterable column of the tree maps onto the attribute value that stands in for it.
 *
 * The tree's columns are the entity ones and a value is aliased onto them, so the filter's column
 * selector offers one tick for all three kinds of row — and a server-side search of the values has to
 * translate that tick back. A column with no entry here is not searched: the description is left out
 * because it is stored as rich text, where a substring match would be matching markup as much as
 * words, and the role, type and sensitivity a value has aliased or blank are not its own to match on.
 */
const VALUE_COLUMN_FOR_TREE_COLUMN = {
  entity_name: 'attribute_value_name',
};

/**
 * Local storage key the tree/flat toolbar control persists its value under. Shared by the Glossary
 * and the Entity asset tables, so the mode chosen on one follows the user to the other.
 */
export const ASSET_LIST_MODE_KEY = 'asset_list_mode';

/** The two ways the asset tables present their rows. */
export const ASSET_LIST_MODE = { tree: 'tree', flat: 'flat' };

/**
 * Reads the list mode last chosen, so a table opens in the mode it was left in. The toolbar control
 * writes the value with JSON.stringify, so it is parsed back here; anything else falls back to the
 * tree, which is the default view.
 *
 * @returns {string} ASSET_LIST_MODE.tree or ASSET_LIST_MODE.flat
 */
export const getStoredAssetListMode = () => {
  try {
    return JSON.parse(localStorage.getItem(ASSET_LIST_MODE_KEY)) === ASSET_LIST_MODE.flat
      ? ASSET_LIST_MODE.flat
      : ASSET_LIST_MODE.tree;
  } catch (error) {
    // Nothing stored, or something that is not the value this writes — open as a tree.
    return ASSET_LIST_MODE.tree;
  }
};

// What getHierarchyPath joins an entity path with. Splitting on it is what leaves the assets above
// a row when its own name — always the last segment — is removed.
const ASSET_PATH_SEPARATOR = ' > ';

/**
 * The assets above one row: its entity_path without its own name, e.g. "Sales > Customer" for the
 * attribute "Country" under the entity "Customer".
 *
 * @param {{entity_path?: string}} row - A row carrying the path fetchGlossaryAssetRows resolved
 * @returns {string|null} The path above the row, or null when nothing is above it. Null rather than
 *   an empty string: that is what tells the table this row has nothing to show in the slot.
 */
const getAssetParentPath = row => {
  const separatorIndex = row.entity_path?.lastIndexOf(ASSET_PATH_SEPARATOR) ?? -1;

  return separatorIndex > 0 ? row.entity_path.slice(0, separatorIndex) : null;
};

/**
 * Flattens the tree: every entity and attribute becomes a root row, so the table lists them all at
 * one level, sorted by name, rather than under their parents. Each also carries the path above it,
 * which is what says where a row sits once the nesting that said so is gone.
 *
 * Attribute values keep their parent. A value is read by expanding its attribute and belongs under
 * it wherever that attribute is shown — the same behaviour as the All Attributes tab — so the table
 * must keep its parentIdColumn in both modes.
 *
 * Rows are replaced rather than changed in place: the table matches rows by identity, and a mutated
 * row would not re-render.
 *
 * @param {object[]} rows - The tree's rows: entities and attributes, plus the values read so far
 * @returns {object[]} The same rows, with every parent but a value's removed
 */
export const toFlatAssetRows = rows =>
  rows.map(row =>
    row.object_name === Asset.AttributeValue
      ? row
      : { ...row, parent_unique_id: null, entity_parent_path: getAssetParentPath(row) }
  );

/**
 * Gives each value row the ancestry line its tooltip shows, walked over the tree it is joining.
 *
 * The rows above a value are already on screen — its attribute, and the entities above that — so the
 * chain is resolved against them. A value whose attribute is not there (which cannot happen for a
 * row being expanded, and can for a search match while the tree is still loading) simply keeps its
 * own name.
 *
 * @param {object[]} values - Shaped value rows
 * @param {object[]} treeRows - The tree's current rows
 * @returns {object[]} The same rows, each with entity_path
 */
export const withValuePaths = (values, treeRows) => {
  // Keyed for the walk rather than scanned per level: a batch of values against a glossary of
  // thousands of rows would otherwise be a scan per value per level.
  const byId = new Map([...(treeRows || []), ...values].map(row => [row.unique_id, row]));

  return values.map(value => ({
    ...value,
    entity_path: getHierarchyPath(value, byId, Asset.Entity),
  }));
};

/**
 * Loads the values of one or more attributes — one row opening, or every open row being re-read
 * after a change.
 *
 * A list rather than an id because re-reading is the common case and asking per attribute is a
 * request per open row. The whole list goes into one `in` clause, which the API binds as a single
 * array parameter. What bounds it is the URL: an id list of a few thousand outgrows the query
 * string, and a POST endpoint is the answer if that is ever reached.
 *
 * `toRow` is how a list that shows values under a different set of columns shapes them: the glossary
 * trees alias a value onto the entity_* keys their columns read, while the flat attribute list aliases
 * it onto the attribute_* ones. Everything else about the read is the same.
 *
 * @param {object} args
 * @param {Array<number|string>} args.attributeIds
 * @param {Function} [args.toRow] - Row shaper; defaults to the glossary trees' one.
 * @param {AbortSignal} [args.signal]
 * @returns {Promise<object[]>} Shaped rows of every attribute asked for, as the API returned them.
 */
export const fetchAttributeValues = async ({
  attributeIds,
  toRow = toAttributeValueRow,
  signal,
} = {}) => {
  const ids = (attributeIds || []).map(Number).filter(id => !isNaN(id));

  // Nothing to ask for. Without this the clause would be an empty `in ()`, which is a 400.
  if (ids.length === 0) return [];

  const query = new URLSearchParams({ $filter: `attribute_id in (${ids.join(',')})` });

  return attributeValueService
    .getAll(query.toString(), signal)
    .then(res => (res?.attribute_values || []).map(toRow));
};

/**
 * Searches attribute values across a whole tree, for the filter box.
 *
 * The values of an attribute are only loaded when it is expanded, so a filter cannot find them in the
 * browser — this is the request that does it instead. `scope` is what confines the search to the
 * assets the table is showing, since the endpoint sees every value in the installation.
 *
 * @param {object} args
 * @param {string} args.scope - A $filter expression naming the list, e.g. `glossary_id eq 27`.
 * @param {string} args.term - What the user typed.
 * @param {string[]} args.fields - The list's columns that the filter's column selector has ticked.
 * @param {object} [args.columnMap] - Which value column each of those columns stands for; defaults
 *   to the glossary trees' aliasing.
 * @param {Function} [args.toRow] - Row shaper; defaults to the glossary trees' one.
 * @param {number} [args.top] - Maximum number of matches to ask for.
 * @param {AbortSignal} [args.signal]
 * @returns {Promise<object[]>} Rows, each placed under its attribute by parent_unique_id.
 */
export const searchAttributeValues = async ({
  scope,
  term,
  fields,
  columnMap = VALUE_COLUMN_FOR_TREE_COLUMN,
  toRow = toAttributeValueRow,
  top,
  signal,
} = {}) => {
  const columns = [
    ...new Set((fields || []).map(field => columnMap[field]).filter(column => column)),
  ];

  // Nothing searchable was asked for, so there is nothing to ask the server.
  if (!scope || !term || columns.length === 0) return [];

  // OData escapes a quote inside a string literal by doubling it. Case-insensitively, via tolower(),
  // because the filter box has always matched that way.
  const literal = String(term).toLowerCase().replaceAll("'", "''");
  const matches = columns.map(column => `contains(tolower(${column}),'${literal}')`).join(' or ');

  const query = new URLSearchParams({ $filter: `${scope} and (${matches})` });
  if (top) query.set('$top', top);

  return attributeValueService
    .getAll(query.toString(), signal)
    .then(res => (res?.attribute_values || []).map(toRow));
};

/**
 * Loads a glossary's assets as one set of tree rows, with the inbound reference counts for them.
 *
 * Attributes are read from the attribute resource and placed by their own parent_entity_id. They used
 * to be pulled in through the generic reference table, which meant an attribute appeared under every
 * entity that happened to mention it and could not appear at all unless something did.
 *
 * Each attribute also carries how many attribute values it has, which is what decides whether it
 * offers an expander. The values themselves are not read here — a glossary can hold a great many of
 * them, and they are loaded per attribute when one is opened.
 *
 * @param {object} args
 * @param {number|string} args.glossaryId
 * @returns {Promise<{ rows: object[], referenceCounts: object[] }>} `rows` ready for the table, and
 *   `referenceCounts` tagged with to_object_name so a caller can match them to either kind.
 */
export const fetchGlossaryAssetRows = async ({ glossaryId }) => {
  const countsFor = objectName =>
    referenceService
      .getToReferencesCountPerChild(objectName, Asset.Glossary, glossaryId)
      .then(res =>
        (res?.to_references_count || []).map(x => ({ ...x, to_object_name: objectName }))
      );

  const [entities, entityCounts, attributes, attributeCounts, valueCounts] = await Promise.all([
    entityService
      .getByGlossaryId(glossaryId)
      .then(res => (res?.entities || []).map(x => toEntityRow(x, glossaryId))),
    countsFor(Asset.Entity),
    attributeService
      .getByGlossaryId(glossaryId)
      .then(res => (res?.attributes || []).map(toAttributeRow)),
    countsFor(Asset.Attribute),
    // Counted for every attribute in one request rather than per glossary: the endpoint returns a row
    // only for an attribute that has values, so the whole answer is small.
    attributeService
      .getAttributeValueCount()
      .then(
        res =>
          new Map(
            (res?.attribute_value_count || []).map(x => [x.attribute_id, x.attribute_value_count])
          )
      ),
  ]);

  const rows = [
    ...entities,
    ...attributes.map(attribute => ({
      ...attribute,
      attribute_value_count: valueCounts.get(attribute.attribute_id) || 0,
    })),
  ];

  return {
    // The path is resolved against the combined set, so an attribute's path includes the entities
    // above it.
    rows: rows.map(row => ({ ...row, entity_path: getHierarchyPath(row, rows, Asset.Entity) })),
    referenceCounts: [...entityCounts, ...attributeCounts],
  };
};

/**
 * Looks up every glossary asset that already carries a given name, in any glossary.
 *
 * Deliberately not scoped to one glossary, even though that is the scope the database enforces: the
 * form needs to tell a clash inside the chosen glossary — which will be rejected — from the same name
 * existing in another one, which is allowed and is how the layered-glossary pattern works.
 *
 * Both kinds are looked up because a user picking a role does not think in tables. Note that an entity
 * and an attribute may legitimately share a name within one glossary: the unique indexes are per table.
 *
 * @param {object} args
 * @param {string} args.name - The name being typed. An empty name resolves to no matches.
 * @param {number} [args.excludeAttributeId] - Attribute being edited, excluded from its own result.
 * @param {number} [args.excludeEntityId] - Entity being edited, excluded from its own result.
 * @param {AbortSignal} [args.signal]
 * @returns {Promise<{attributes: object[], entities: object[]}>}
 */
export const fetchAssetsWithSameName = async ({
  name,
  excludeAttributeId,
  excludeEntityId,
  signal,
} = {}) => {
  // Coerced rather than assumed to be a string: this is called straight from form change handlers.
  const term = name === undefined || name === null ? '' : String(name);
  if (term.trim() === '') return { attributes: [], entities: [] };

  // The API takes the literal string 'undefined' to mean "exclude nothing", and a name goes into the
  // path, so percent signs have to survive encodeURIComponent.
  const encoded = encodeURIComponent(term.replaceAll('%', '%25'));
  const [attributes, entities] = await Promise.all([
    attributeService
      .getByName(encoded, excludeAttributeId ?? 'undefined', undefined, signal)
      .then(res => res?.attributes || []),
    entityService
      .getByName(encoded, excludeEntityId ?? 'undefined', undefined, signal)
      .then(res => res?.entities || []),
  ]);

  return { attributes, entities };
};

/**
 * Turns the result of fetchAssetsWithSameName into the validation shown under the name field.
 *
 * Severity follows what the database will do with the save:
 *  - an asset of the same kind in the same glossary is a hard clash, since that is exactly what the
 *    per-glossary unique index forbids — reported as 'danger';
 *  - anything else is legal and merely worth knowing: the same name in another glossary, or the other
 *    kind of asset in this one — reported as 'info'.
 *
 * "Same kind" follows the role currently chosen in the form rather than what the asset is today, so
 * switching the role re-evaluates the severity without another request.
 *
 * @param {object} args
 * @param {{attributes: object[], entities: object[]}} args.matches - From fetchAssetsWithSameName.
 * @param {number} args.glossaryId - The glossary chosen in the form.
 * @param {string} args.targetObjectName - Asset.Entity or Asset.Attribute, from the chosen role.
 * @returns {{valid: string, message: string, assets: object[]}|undefined} undefined when the name is free.
 */
export const getSameNameValidation = ({ matches, glossaryId, targetObjectName } = {}) => {
  const assets = [
    ...(matches?.entities || []).map(entity => ({
      objectName: Asset.Entity,
      name: entity.entity_name,
      description: entity.entity_description,
      icon: Asset.Entity,
      glossaryId: entity.glossary_id,
      glossaryName: entity.glossary_name,
    })),
    ...(matches?.attributes || []).map(attribute => ({
      objectName: Asset.Attribute,
      name: attribute.attribute_name,
      description: attribute.attribute_description,
      icon: attribute.attribute_role_name,
      glossaryId: attribute.glossary_id,
      glossaryName: attribute.glossary_name,
    })),
  ];

  if (assets.length === 0) return undefined;

  const clashes = assets.filter(
    asset =>
      asset.objectName === targetObjectName && Number(asset.glossaryId) === Number(glossaryId)
  );

  if (clashes.length > 0) {
    return {
      valid: 'danger',
      message: `A ${AssetName[targetObjectName]} with this name already exists in this glossary. Saving will fail until the name is changed.`,
      assets,
    };
  }

  return {
    valid: 'info',
    message: _describeSameNameAssets(assets, glossaryId),
    assets,
  };
};

/**
 * One line naming what already carries this name and where, for the informational case.
 *
 * Says the kind and the place when they are unambiguous — "1 other Attribute with this name exists in
 * this glossary" — and generalises when they are not, since the tooltip lists every match anyway. The
 * kind is named because an entity and an attribute sharing a name is legal but still worth noticing,
 * and the place is named because whether it is this glossary or another is the whole distinction.
 *
 * @param {object[]} assets - Normalised matches.
 * @param {number} glossaryId - The glossary chosen in the form.
 * @returns {string}
 */
const _describeSameNameAssets = (assets, glossaryId) => {
  const isHere = asset => Number(asset.glossaryId) === Number(glossaryId);
  const kinds = [...new Set(assets.map(asset => asset.objectName))];
  const glossaryNames = [...new Set(assets.map(asset => asset.glossaryName))];

  // Kind: named when they are all the same, otherwise the neutral word.
  const noun =
    kinds.length === 1
      ? assets.length === 1
        ? AssetName[kinds[0]]
        : AssetNamePlural[kinds[0]]
      : 'assets';

  // Place: named when they are all in one glossary, which is the common case.
  let place = '';
  if (assets.every(isHere)) {
    place = ' in this glossary';
  } else if (glossaryNames.length === 1) {
    place = ` in the glossary "${glossaryNames[0]}"`;
  } else if (!assets.some(isHere)) {
    place = ` in ${glossaryNames.length} other glossaries`;
  }

  return `${assets.length} other ${noun} with this name ${assets.length === 1 ? 'exists' : 'exist'}${place}`;
};
